import { grpc } from "@improbable-eng/grpc-web";
import { UnaryOutput } from "@improbable-eng/grpc-web/dist/typings/unary";
import { ProtobufMessage } from "@improbable-eng/grpc-web/dist/typings/message";
import * as google_protobuf_timestamp_pb from "google-protobuf/google/protobuf/timestamp_pb";
import { Metadata } from "@improbable-eng/grpc-web/dist/typings/metadata";
import { isEmpty } from "lodash";
import { ClientListResponse, UserListItem } from "@/types";

import AuthUtils from "@/utils/auth-utils";

import { Admin as Service } from "@/proto/admin/all_pb_service";
import * as ServiceProto from "@/proto/admin/all_pb";
import {
  BankSecretRow,
  ClientInfo,
  PersonInfo,
  ClientListItem,
  ClientSecret,
  CoreProducts,
  CreateBankSecretRequest,
  GetClientListRequest,
  Transaction,
  TransactionStatus,
  GetTransactionsRequest,
  TransactionFile,
  EDocumentTypeMap,
  TFileUpload,
} from "@/types";
import {
  DESTINATION_BANKS,
  SOURCE_BANKS,
  STATEMENT_BANK_CODE_MAPPING,
} from "@/constant";
import { uploadFile } from "../request-util";
import {
  EUserTypes,
  EDomicileTypes,
  ECountryCode,
} from "@/pagesCom/ClientInfo/index.dto";
import {
  KYCStatus,
  OrgType,
  DomicileType,
  PICType,
  PICTypeMap,
} from "@/proto/identity/identity/all_pb";
import {
  handleRequestBySr,
  parsePhoneNumber,
  removePlusSign,
} from "@/utils/common-utils";
import { formatPreviousDocuments, uploadSupportingDocuments } from "./utils";
import {
  ICompanyFormData,
  ICompanyIDFormData,
  IPersonalFormData,
  IPersonalIDFormData,
  TSupportingDocsFormData,
} from "@/pagesCom/ClientInfo/EditDocuments/index.dto";
import { kycApplicationFilterMap } from "@/utils/client-utils";

export default class ClientService {
  constructor(private baseURL: string, private token: string) {}

  public async getClients(
    search?: GetClientListRequest
  ): Promise<ClientListResponse> {
    const request = new ServiceProto.GetClientDetailsRequest();

    request.setPage(search.page);
    request.setPagesize(search.pageSize);

    if (search) {
      const searchParams = new ServiceProto.GetClientSearch();
      if (search.searchtext) {
        searchParams.setSearchtext(search.searchtext);
        searchParams.setSearchtype(search.searchtype);
        request.setTextsearch(searchParams);
      }

      if (search.filter) {
        switch (search.filter.access) {
          case "all":
            request.setHasliveaccessfilter(ServiceProto.LiveAccessFilter.NONE);
            break;
          case "live":
            request.setHasliveaccessfilter(ServiceProto.LiveAccessFilter.LIVE);
            break;
          case "sandbox":
            request.setHasliveaccessfilter(
              ServiceProto.LiveAccessFilter.SANDBOX
            );
            break;
          default:
            break;
        }
        request.setCountryfilter(search.filter.countries);
        if (search.filter.kycApplication !== "all") {
          request.setKycstatusfilterList(
            kycApplicationFilterMap[search.filter.kycApplication]
          );
        }
      }
    }

    const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetClients, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with GetClients: code=${status} msg=${statusMessage}`
      );
    }
    const decoded = message as ServiceProto.GetClientDetailsResponse;
    const total = decoded.getTotalcount();

    const clientList = decoded
      .getClientdetailsList()
      .map<ClientListItem>((item) => {
        const transactionData = item.getTransactioncountdetails();
        return {
          id: item.getId(),
          name: item.getName(),
          country: item.getCountry(),
          kycStatus: item.getKycstatus(),
          transactions:
            (transactionData?.getDirect() ?? 0) +
            (transactionData?.getDisburse() ?? 0) +
            (transactionData?.getStatement() ?? 0),
          logo: item.getLogourl(),
          access: item.getIslive() ? "Live" : "Sandbox",
        };
      });

    return {
      clientList,
      total,
    };
  }

  private async getClientBusinessOwner(
    clientId: string
  ): Promise<ServiceProto.BusinessOwner.AsObject> {
    const request = new ServiceProto.GetBusinessOwnerInformationRequest();

    request.setOrgid(clientId);

    const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetBusinessOwner, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with GetBusinessOwner: code=${status} msg=${statusMessage}`
      );
    }

    const data = message as ServiceProto.BusinessOwner;
    return data.toObject();
  }

  public async updateClientBusinessOwner(
    orgId: string,
    payload: ServiceProto.BusinessOwner.AsObject
  ): Promise<boolean> {
    const request = new ServiceProto.UpdateBusinessOwnerInformationRequest();

    request.setOrgid(orgId);

    request.setFullname(payload.fullname);
    request.setPlaceofbirth(payload.placeofbirth);
    request.setDateofbirth(payload.dateofbirth);
    request.setGender(payload.gender);
    request.setOccupation(payload.occupation);
    request.setPhonenumber(payload.phonenumber);
    request.setPhonenumbercountrycode(
      payload.phonenumbercountrycode
        ? removePlusSign(payload.phonenumbercountrycode)
        : ""
    );
    request.setEmail(payload.email);
    request.setIsuscitizen(payload.isuscitizen);
    request.setCitizenship(payload.citizenship);
    request.setCurrentaddressline1(payload.currentaddressline1);
    request.setCurrentaddressline2(payload.currentaddressline2);
    request.setCurrentcountry(payload.currentcountry);
    request.setCurrentregion(payload.currentregion);
    request.setCurrentcity(payload.currentcity);
    request.setCurrentzipcode(payload.currentzipcode);
    request.setRegisteredaddressline1(payload.registeredaddressline1);
    request.setRegisteredaddressline2(payload.registeredaddressline2);
    request.setRegisteredcountry(payload.registeredcountry);
    request.setRegisteredregion(payload.registeredregion);
    request.setRegisteredcity(payload.registeredcity);
    request.setRegisteredzipcode(payload.registeredzipcode);
    request.setTaxidentificationnumber(payload.taxidentificationnumber);

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.UpdateBusinessOwner, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });
    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with UpdateBusinessOwner: code=${status} msg=${statusMessage}`
      );
    }
    return true;
  }

  public async getSupportingDocuments({
    clientId,
    domicileType,
    country,
    userType,
  }: {
    clientId: string;
    domicileType: any;
    country: any;
    userType: any;
  }): Promise<ClientInfo["supportingDocsData"]> {
    const request = new ServiceProto.GetSupportingDocumentsRequest();

    request.setOrgid(clientId);
    request.setDomiciletype(domicileType!);
    request.setCountry(country);
    request.setOrgtype(userType!);

    const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetSupportingDocuments, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with GetSupportingDocuments: code=${status} msg=${statusMessage}`
      );
    }

    const data = message as ServiceProto.GetSupportingDocumentsResponse;

    return {
      documentNumber: data.getDocumentidnumber(),
      documentType: data.getDocumenttype() as EDocumentTypeMap,
      taxIdNumber: data.getTaxidentificationnumber(),
      documents: formatPreviousDocuments(data.getOrgdocumentsList()),
      isShareholdersDeclared: data.getIsshareholder(),
      isDirectorsDeclared: data.getIscompanydirectors(),
      previousDocumentIds: data
        .getOrgdocumentsList()
        .map((item) => item.getId()),
    };
  }

  public async getFinancePIC(clientId: string): Promise<{
    authorizedsignatoriesList: PersonInfo[];
    billingpicsList: PersonInfo[];
  }> {
    const request = new ServiceProto.GetFinancePICRequest();

    request.setOrgid(clientId);

    const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetFinancePIC, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with GetFinancePIC: code=${status} msg=${statusMessage}`
      );
    }

    const data = message as ServiceProto.GetFinancePICResponse;
    const picdocumentsList = data
      .getPicdocumentsList()
      .map((data) => data.toObject());

    const mappingPICData = (
      financePIC: ServiceProto.FinancePIC[],
      picType: PICTypeMap[keyof PICTypeMap]
    ) => {
      return financePIC.map((data) => {
        const poaFile = picdocumentsList.find(
          (doc) => doc.id === data.getPowerofattorneyfileid()
        );
        const idFile = picdocumentsList.find(
          (doc) => doc.id === data.getIdentificationdocumentfileid()
        );
        const dirIdFile = picdocumentsList.find(
          (doc) => doc.id === data.getIdentificationdocumentfileid()
        );

        return {
          id: data.getId(),
          roleType: picType,
          picIndex: data.getPicindex(),
          fullName: data.getFullname(),
          role: data.getRole(),
          phoneNumber: data.getPhonenumber(),
          phoneNumberCountryCode: data.getPhonenumbercountrycode() || "",
          email: data.getEmail(),
          isDirector: data.getDirectorofcompany(),
          [EDocumentTypeMap.POA_DOC]: {
            fileId: poaFile?.id,
            fileUrl: poaFile?.signedurl,
            fileName: poaFile?.filename,
          },
          [EDocumentTypeMap.ID_DOC]: {
            fileId: idFile?.id,
            fileUrl: idFile?.signedurl,
            fileName: idFile?.filename,
          },
          [EDocumentTypeMap.DIR_ID_DOC]: {
            fileId: dirIdFile?.id,
            fileUrl: dirIdFile?.signedurl,
            fileName: dirIdFile?.filename,
          },
        };
      });
    };

    const authorizedsignatoriesList = mappingPICData(
      data.getAuthorizedsignatoriesList(),
      PICType.AUTHORIZED_SIGNATORY
    );

    const billingpicsList = mappingPICData(
      data.getBillingpicsList(),
      PICType.BILLING
    );

    return {
      authorizedsignatoriesList: authorizedsignatoriesList.sort(
        (a, b) => a.picIndex - b.picIndex
      ),
      billingpicsList: billingpicsList.sort((a, b) => a.picIndex - b.picIndex),
    };
  }

  public async getClientInfo(clientId: string): Promise<ClientInfo | null> {
    const request = new ServiceProto.GetClientInfoRequest();

    request.setId(clientId);

    const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetClientInfo, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with GetClientInfo: code=${status} msg=${statusMessage}`
      );
    }

    const data = message as ServiceProto.ClientDetail;
    if (!data) return null;
    // TODO: Update later to include other API call for Client Info
    const [businessOwnerRs, supportingDocumentsRs, financePICRs] =
      await Promise.allSettled([
        this.getClientBusinessOwner(clientId),
        this.getSupportingDocuments({
          clientId,
          country: data?.getCountry(),
          domicileType: data?.getDomiciletype(),
          userType: data?.getOrgtype(),
        }),
        this.getFinancePIC(clientId),
      ]);

    const countryCode = removePlusSign(data?.getPhonenumbercountrycode());
    const parsedPhonenumber = parsePhoneNumber(
      data?.getPhonenumber(),
      countryCode
    );

    return {
      name: data?.getName() || "",
      role: data.getRole(),
      postalCode: data.getPostalcode(),
      addressline1: data?.getAddressline1() || "",
      addressline2: data?.getAddressline2() || "",
      city: data?.getCity() || "",
      province: data?.getRegion() || "",
      country: data?.getCountry() || "",
      phoneNumber: data?.getPhonenumber() ? parsedPhonenumber.phoneNumber : "",
      phoneNumberCountryCode: data?.getPhonenumbercountrycode() || "",
      logoUrl: data.getLogourl(),
      isLive: data.getIslive(),
      taxId: data.getTaxidentificationnumber() || "",
      industry: data.getIndustry() || "",
      businessType: data.getBusinesstype() || "",
      noEmployees: data.getNoemployees() || "",
      transactionPerMonth: data.getTransactionspermonth() || "",
      revokeReason: !data.getIslive() ? data.getRevokereason() : null,
      authorizedSignatory:
        financePICRs.status === "fulfilled"
          ? financePICRs.value.authorizedsignatoriesList
          : [],
      financePIC:
        financePICRs.status === "fulfilled"
          ? financePICRs.value.billingpicsList
          : [],
      businessProfile: data.getWebsite(),
      userType:
        EUserTypes[
          Object.keys(OrgType)[
            Object.values(OrgType).indexOf(data.getOrgtype())
          ] as keyof typeof EUserTypes
        ] || EUserTypes.COMPANY,
      domicileType:
        EDomicileTypes[
          Object.keys(DomicileType)[
            Object.values(DomicileType).indexOf(data.getDomiciletype())
          ] as keyof typeof EDomicileTypes
        ] || EDomicileTypes.FOREIGN,
      kycStatus: data.getKycstatus(),
      liveDate:
        data.getKycstatus() === KYCStatus.APPROVE
          ? data.getLivedate()?.toDate()
          : null,
      companyProfile: data?.getCompanyprofile() || "",
      dateOfEstablishment: data?.getDateofestablishment() || "",
      placeOfEstablishment: data?.getPlaceofestablishment() || "",
      hasPhysicalStore: data?.getHasphysicalstore() || false,
      businessOwner:
        businessOwnerRs.status === "fulfilled"
          ? businessOwnerRs.value
          : undefined,
      subIndustry: data.getSubindustry() || "",
      supportingDocsData:
        supportingDocumentsRs.status === "fulfilled"
          ? supportingDocumentsRs.value
          : undefined,
    };
  }

  public async updateCompanyInformation(
    clientId: string,
    payload: ClientInfo
  ): Promise<any> {
    const request = new ServiceProto.UpdateCompanyInformationRequest();
    request.setOrgid(clientId);
    request.setName(payload.name);
    request.setBusinesstype(payload.businessType!);
    request.setRole(payload.role!);
    request.setTransactionspermonth(payload.transactionPerMonth!);
    request.setNoemployees(payload.noEmployees!);
    request.setPostalcode(String(payload.postalCode) || "");
    request.setAddressline1(payload.addressline1);
    request.setAddressline2(payload.addressline2 || "");
    request.setCity(payload.city);
    request.setRegion(payload.province);
    request.setPhonenumber(payload.phoneNumber);
    request.setPhonenumbercountrycode(
      payload.phoneNumberCountryCode
        ? removePlusSign(payload.phoneNumberCountryCode)
        : ""
    );
    request.setTaxidentificationnumber(payload.taxId || "");
    request.setIndustry(payload.industry || "");
    request.setWebsite(payload.businessProfile || "");
    request.setSubindustry(payload.subIndustry || "");
    request.setHasphysicalstore(payload.hasPhysicalStore || false);
    request.setCompanyprofile(payload.companyProfile || "");
    request.setPlaceofestablishment(payload.placeOfEstablishment || "");
    payload.dateOfEstablishment &&
      request.setDateofestablishment(
        String(new Date(payload.dateOfEstablishment).getTime() / 1000)
      );

    if (payload?.logo && payload?.logo instanceof File) {
      await uploadFile({
        path: "upload-file/org-logo",
        file: payload!.logo as File,
        orgId: clientId,
      });
    }

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.UpdateCompanyInformation, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with UpdateCompanyInformation: code=${status} msg=${statusMessage}`
      );
    }

    return true;
  }

  public async getClientTransactions(
    payload: GetTransactionsRequest
  ): Promise<Transaction[]> {
    const { clientId, env, product, filters } = payload;
    const request = new ServiceProto.GetTransactionsRequest();
    request.setOrgid(clientId);
    request.setEnv(env);
    request.setService(product);

    if (!isEmpty(filters)) {
      const requestFilters = new ServiceProto.TransactionFilters();
      if (filters?.accountNo) {
        requestFilters.setDestaccnumber(filters?.accountNo);
      }
      if (filters?.minAmount) {
        requestFilters.setMinamount(filters?.minAmount);
      }
      if (filters?.maxAmount) {
        requestFilters.setMaxamount(filters?.maxAmount);
      }

      if (filters?.fromDate) {
        requestFilters.setDatefrom(
          google_protobuf_timestamp_pb.Timestamp.fromDate(filters.fromDate)
        );
      }

      if (filters?.toDate) {
        requestFilters.setDateto(
          google_protobuf_timestamp_pb.Timestamp.fromDate(filters.toDate)
        );
      }

      request.setTransactionfilters(requestFilters);
    }

    const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetTransactions, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with GetTransactions: code=${status} msg=${statusMessage}`
      );
    }
    const decoded = message as ServiceProto.GetTransactionsResponse;

    return decoded.getTransactionsList().map<Transaction>((item) => {
      let status;
      let date = item.getCreated();
      const rawStatus = item.getStatus();
      const sourceBankName = item.getSourcebankname();
      const destBankName = item.getDestbankname();
      const bankCode = item.getBankcode();

      if (!date) {
        date = item.getStartdate();
      }

      if (rawStatus && rawStatus.includes("TransferStatus")) {
        // For Direct
        status = rawStatus.split(/TransferStatus_/)[1] || "";
      } else {
        status = rawStatus;
      }

      let sourceBank =
        SOURCE_BANKS.find((item) => item.name === sourceBankName)?.title ||
        "N/A";
      const destBank =
        DESTINATION_BANKS.find((item) => item.name === destBankName)?.title ||
        "N/A";

      if (product === CoreProducts.STATEMENT) {
        sourceBank = STATEMENT_BANK_CODE_MAPPING[bankCode] || "N/A";
      }

      return {
        id: item.getId(),
        date: date?.getSeconds(),
        from: {
          accountNumber: item.getSourceaccountnumber(),
          bankName: sourceBank,
        },
        to: {
          accountNumber: item.getDestaccountnumber(),
          bankName: destBank,
        },
        status: status as TransactionStatus,
        amount: item.getAmount(),
        currency: item.getCurrency(),
      };
    });
  }

  public async exportClientTransactions(
    payload: GetTransactionsRequest
  ): Promise<TransactionFile | null> {
    const { clientId, env, product, filters } = payload;
    const request = new ServiceProto.DownloadTransactionsRequest();

    request.setOrgid(clientId);
    request.setEnv(env);
    request.setService(product);

    if (!isEmpty(filters)) {
      const requestFilters = new ServiceProto.TransactionFilters();
      if (filters?.accountNo) {
        requestFilters.setDestaccnumber(filters?.accountNo);
      }
      if (filters?.minAmount) {
        requestFilters.setMinamount(filters?.minAmount);
      }
      if (filters?.maxAmount) {
        requestFilters.setMaxamount(filters?.maxAmount);
      }

      if (filters?.fromDate) {
        requestFilters.setDatefrom(
          google_protobuf_timestamp_pb.Timestamp.fromDate(filters.fromDate)
        );
      }

      if (filters?.toDate) {
        requestFilters.setDateto(
          google_protobuf_timestamp_pb.Timestamp.fromDate(filters.toDate)
        );
      }

      request.setTransactionfilters(requestFilters);
    }

    const {
      code,
      message,
    }: {
      code: grpc.Code;
      message: ProtobufMessage | null;
      trailers: Metadata;
    } = await new Promise((resolve) => {
      let protoMessage: ProtobufMessage | null = null;

      grpc.invoke(Service.DownloadTransactions, {
        request,
        host: this.baseURL,
        onMessage: (message: ProtobufMessage) => {
          protoMessage = message;
        },
        onEnd: (code: grpc.Code, message: string, trailers: Metadata) => {
          resolve({
            code,
            trailers,
            message: protoMessage,
          });
        },
        metadata: AuthUtils.getMetadata(this.token),
      });
    });

    if (code !== grpc.Code.OK) {
      throw new Error(`Error with DownloadTransactions: code=${status}`);
    }
    const decoded = message as ServiceProto.DownloadTransactionsResponse;
    const file = decoded.getTransactionsfile();
    if (file) {
      return {
        name: file.getFilename(),
        content: file.getData_asB64(),
        size: file.getSize(),
      };
    }

    return null;
  }

  public async getClientIntegrations(orgId: string): Promise<BankSecretRow[]> {
    const request = new ServiceProto.GetBankSecretsRequest();

    request.setOrgid(orgId);

    const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetBankSecrets, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with GetBankSecrets: code=${status} msg=${statusMessage}`
      );
    }
    const decoded = message as ServiceProto.GetBankSecretsResponse;

    return decoded.getBanksecretsList().map((item) => {
      return {
        id: item.getId(),
        bankCode: item.getBank(),
        createdAt: item.getCreated()?.getSeconds(),
        createdBy: item.getCreatedby(),
      };
    });
  }

  public async getClientIntegrationInfo(id: string): Promise<ClientSecret> {
    const request = new ServiceProto.GetBankSecretRequest();

    request.setId(id);

    const { message, status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetBankSecret, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with GetBankSecret: code=${status} msg=${statusMessage}`
      );
    }
    const decoded = message as ServiceProto.GetBankSecretResponse;
    const bankSecret = decoded.getBanksecret();

    return {
      environment: bankSecret.getEnvironment(),
      id: bankSecret.getId(),
      bank: bankSecret.getBank(),
      clientId: bankSecret.getClientid(),
      clientSecret: bankSecret.getClientsecret(),
      apiKey: bankSecret.getApikey(),
      publicKey: bankSecret.getPublickey_asB64(),
      publicKeyFilename: bankSecret.getPublickeyfilename(),
      accessKey: bankSecret.getAccesskey(),
      signKey: bankSecret.getSignkey(),
      notifyKey: bankSecret.getNotifykey(),
      partnerId: bankSecret.getPartnerid() || bankSecret.getMerchantcode(),
      partnerSecret: bankSecret.getPartnersecret() || bankSecret.getPassword(),
      merchantID: bankSecret.getMerchantid(),
    };
  }

  public async upsertClientIntegration(
    payload: CreateBankSecretRequest
  ): Promise<boolean> {
    const request = new ServiceProto.CreateBankSecretRequest();

    const bankSecret = new ServiceProto.BankSecret();

    // Update bank secret
    if (payload.id) {
      bankSecret.setId(payload.id);
    }

    bankSecret.setOrgid(payload.orgId);
    bankSecret.setEnvironment(payload.environment);
    bankSecret.setBank(payload.bank);
    payload.clientId && bankSecret.setClientid(payload.clientId);
    payload.clientSecret && bankSecret.setClientsecret(payload.clientSecret);
    payload.apiKey && bankSecret.setApikey(payload.apiKey);
    payload.publicKey && bankSecret.setPublickey(payload.publicKey);
    payload.publicKeyFilename &&
      bankSecret.setPublickeyfilename(payload.publicKeyFilename);
    payload.accessKey && bankSecret.setAccesskey(payload.accessKey);
    payload.signKey && bankSecret.setSignkey(payload.signKey);
    payload.notifyKey && bankSecret.setNotifykey(payload.notifyKey);
    payload.partnerId && bankSecret.setPartnerid(payload.partnerId);
    payload.partnerSecret && bankSecret.setPartnersecret(payload.partnerSecret);
    payload.merchantID && bankSecret.setMerchantid(payload.merchantID);

    request.setBanksecret(bankSecret);

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.CreateBankSecret, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with CreateBankSecret: code=${status} msg=${statusMessage}`
      );
    }
    return true;
  }

  public async deleteClientIntegration(id: string): Promise<boolean> {
    const request = new ServiceProto.DeleteBankSecretRequest();

    request.setId(id);

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.DeleteBankSecret, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with DeleteBankSecret: code=${status} msg=${statusMessage}`
      );
    }
    return true;
  }

  public async updateOrgLiveAccess(
    orgId: string,
    isLive: boolean,
    reason: string | null = null
  ): Promise<boolean> {
    const request = new ServiceProto.UpdateOrgIsLiveRequest();

    request.setOrgid(orgId);
    request.setIslive(isLive);

    if (!isLive && reason) {
      request.setRevokereason(reason);
    }

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.UpdateOrgIsLive, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with UpdateOrgIsLive: code=${status} msg=${statusMessage}`
      );
    }
    return true;
  }

  public async checkLiveAccessPermission(): Promise<boolean> {
    const request = new ServiceProto.IsUserAuthorizedRequest();

    request.setEndpoint("openbank.v1.admin.Admin/UpdateOrgIsLive");

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.IsUserAuthorized, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    switch (status) {
      case grpc.Code.OK:
        return true;
      case grpc.Code.PermissionDenied:
        return false;
      default:
        throw new Error(
          `Error with IsUserAuthorized: code=${status} msg=${statusMessage}`
        );
    }
  }

  public async submitDocuments(
    orgId: string,
    userType: EUserTypes,
    country: ECountryCode,
    domicile: EDomicileTypes,
    payload: TSupportingDocsFormData,
    previousDocumentIds: string[]
  ) {
    try {
      let uploadedDocsIds = await uploadSupportingDocuments(
        userType,
        country as ECountryCode,
        domicile,
        orgId,
        payload
      );
      uploadedDocsIds = uploadedDocsIds.filter(Boolean);

      // remove deleted files
      const removedDocsIds: string[] = [];
      previousDocumentIds.forEach((id) => {
        if (!uploadedDocsIds.includes(id)) {
          removedDocsIds.push(id);
        }
      });

      if (removedDocsIds.length) {
        await this.removeDocuments(orgId, removedDocsIds);
      }

      const request = new ServiceProto.SubmitSupportingDocumentsRequest();
      request.setOrgid(orgId);
      request.setFileidsList(uploadedDocsIds as string[]);
      request.setCountry(country);
      request.setDomiciletype(
        domicile === EDomicileTypes.FOREIGN
          ? DomicileType.FOREIGN
          : DomicileType.LOCAL
      );
      request.setOrgtype(
        userType === EUserTypes.COMPANY ? OrgType.COMPANY : OrgType.INDIVIDUAL
      );

      if (userType === EUserTypes.INDIVIDUAL) {
        // For Individiual
        request.setDocumenttype((payload as IPersonalFormData).idType);
        request.setDocumentnumber(
          (payload as IPersonalFormData).documentNumber
        );
        if (country === ECountryCode.ID) {
          const taxNumber =
            domicile === EDomicileTypes.FOREIGN
              ? (payload as IPersonalIDFormData).tinNumber
              : (payload as IPersonalIDFormData).npwpNumber;
          request.setTaxidentificationnumber(taxNumber);
        }
      } else {
        // For Company
        let taxNumber = (payload as ICompanyFormData).tinNumber;

        if (country === ECountryCode.ID) {
          if (domicile === EDomicileTypes.LOCAL) {
            taxNumber = (payload as ICompanyIDFormData).npwpNumber;
          }
          request.setIscompanydirectors(
            (payload as ICompanyIDFormData).isDirectorsDeclared as boolean
          );
          request.setIsshareholder(
            (payload as ICompanyIDFormData).isShareholdersDeclared as boolean
          );
        }
        request.setTaxidentificationnumber(taxNumber);
      }

      const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
        await new Promise((resolve) => {
          grpc.unary(Service.SubmitSupportingDocuments, {
            request,
            host: this.baseURL,
            onEnd: resolve,
            metadata: AuthUtils.getMetadata(this.token),
          });
        });

      if (status !== grpc.Code.OK) {
        throw new Error(
          `Error with SubmitSupportingDocuments: code=${status} msg=${statusMessage}`
        );
      }
    } catch (err) {
      throw new Error((err as any)?.message || "Unkown");
    }
  }

  public async requestResubmitDocs(
    orgId: string,
    payload: ServiceProto.KYCDocumentReview.AsObject[]
  ): Promise<boolean> {
    const request = new ServiceProto.KYCDocumentReviewRequest();

    request.setOrgid(orgId);
    request.setKycdocumentsList(
      payload.map((item) => {
        const data = new ServiceProto.KYCDocumentReview();
        data.setDocumenttype(item.documenttype);
        data.setDocumentsList(
          item.documentsList.map((x) => {
            const document = new ServiceProto.KYCDocument();
            document.setId(x.id);
            document.setFilename(x.filename);
            document.setRemarks(x.remarks);
            document.setTitle(x.title);
            document.setName(x.name);

            return document;
          })
        );

        return data;
      })
    );

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.ReviewKYCDocument, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with ReviewKYCDocument: code=${status} msg=${statusMessage}`
      );
    }
    return true;
  }

  public async submitFinancePIC(
    clientId: string,
    domicileType: EDomicileTypes,
    payload: PersonInfo[]
  ): Promise<void> {
    if (!payload?.length) return;

    const promises = payload.map(async (el) => {
      const request = new ServiceProto.SubmitFinancePICRequest();
      request.setOrgid(clientId);
      el.id && request.setId(el.id!);
      request.setPicindex(el.picIndex!);
      request.setFullname(el.fullName!);
      request.setRole(el.role!);
      request.setPhonenumber(el.phoneNumber!);
      request.setPhonenumbercountrycode(
        el.phoneNumberCountryCode
          ? removePlusSign(el.phoneNumberCountryCode)
          : ""
      );
      request.setEmail(el.email!);
      request.setRoletype(el.roleType!);
      request.setDirectorofcompany(el.isDirector!);
      request.setDomiciletype(
        DomicileType[
          Object.keys(EDomicileTypes)[
            Object.values(EDomicileTypes).indexOf(domicileType)
          ] as keyof typeof DomicileType
        ]
      );

      if (el.isDirector && el.directorIdentificationFileId) {
        request.setIdentificationdocumentfileid(
          el.directorIdentificationFileId!
        );
      } else {
        request.setPowerofattorneyfileid(el.poaFileId!);
        request.setIdentificationdocumentfileid(el.identificationFileId!);
      }

      const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
        await new Promise((resolve) => {
          grpc.unary(Service.SubmitFinancePIC, {
            request,
            host: this.baseURL,
            onEnd: resolve,
            metadata: AuthUtils.getMetadata(this.token),
          });
        });

      if (status !== grpc.Code.OK) {
        throw new Error(
          `Error with submitFinancePIC: code=${status} msg=${statusMessage}`
        );
      }
      const { id: picId } = (
        message as ServiceProto.SubmitFinancePICResponse
      ).toObject();

      const pathPicType =
        el.roleType === PICType.BILLING
          ? "finance-pic"
          : "authorized-signatory";

      const poa =
        (el.poaDoc as TFileUpload[])[0] &&
        uploadFile({
          path: `upload-file/${pathPicType}/power-of-attorney`,
          file: (el.poaDoc as TFileUpload[])[0].file as File,
          orgId: clientId,
          metaData: {
            finance_pic_id: picId,
          },
        });

      const idDoc =
        (el.idDoc as TFileUpload[])[0] &&
        uploadFile({
          path: `upload-file/${pathPicType}/identification-document`,
          file: (el.idDoc as TFileUpload[])[0].file as File,
          orgId: clientId,
          metaData: {
            finance_pic_id: picId,
          },
        });

      const dirIdDoc =
        (el.dirIdDoc as TFileUpload[])[0] &&
        uploadFile({
          path: `upload-file/${pathPicType}/identification-document`,
          file: (el.dirIdDoc as TFileUpload[])[0].file as File,
          orgId: clientId,
          metaData: {
            finance_pic_id: picId,
          },
        });

      const fileDocs = [];
      if (el.isDirector) {
        fileDocs.push(dirIdDoc);
      } else {
        fileDocs.push(poa, idDoc);
      }

      const [err] = await handleRequestBySr(Promise.all(fileDocs));
      if (err)
        throw new Error(
          `Error with upload document for PIC: msg=${err?.message || "Unkown"}`
        );
    });

    const [err] = await handleRequestBySr(Promise.all(promises));
    if (err)
      throw new Error(
        `Error with submitFinancePIC: msg=${err?.message || "Unkown"}`
      );
  }

  public async removeFinancePIC(
    clientId: string,
    picId: string
  ): Promise<void> {
    const request = new ServiceProto.RemoveFinancePICRequest();
    request.setOrgid(clientId);
    request.setId(picId);

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.RemoveFinancePIC, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with RemovePIC: code=${status} msg=${statusMessage}`
      );
    }
  }

  public async removeDocuments(
    clientId: string,
    removedIds: string[]
  ): Promise<void> {
    const request = new ServiceProto.RemoveClientDocumentsRequest();
    request.setOrgid(clientId);
    request.setFileidsList(removedIds);

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.RemoveClientDocuments, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with RemoveClientDocuments: code=${status} msg=${statusMessage}`
      );
    }
  }

  public async getListUsers(orgId: string): Promise<UserListItem[]> {
    const request = new ServiceProto.ListUsersRequest();

    request.setOrgid(orgId);

    const { status, message, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.ListUsers, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with getListUsers: code=${status} msg=${statusMessage}`
      );
    }
    const decoded = message as ServiceProto.ListUsersResponse;

    return decoded.getUsersList().map((user) => ({
      ...user.toObject(),
      created: user.getCreated().toDate(),
      lastlogin: user.getLastlogin()?.toDate(),
    }));
  }

  public async deactivateUser(userId: string): Promise<any> {
    const request = new ServiceProto.RemoveUserRequest();
    request.setUserid(userId);

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.RemoveUser, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with deactivateUser: code=${status} msg=${statusMessage}`
      );
    }
  }
}
